import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { Download, ShieldCheck, X, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const cases = [
  {
    id: "case-01",
    file: "Advik-Case-01-MENA-Family-Office.pdf",
    label: "Case 01",
    tab: "MENA · UAE",
    title: "A MENA family office, custody-ready in ninety days.",
    snapshot: [
      ["Entity", "Multi-generational family office"],
      ["Region", "UAE (DIFC)"],
      ["Mandate", "Institutional digital asset custody + venture desk"],
      ["Modules", "M01 Strategic Finance Office · M02 Innovation Hub"],
    ],
    quote:
      "We came in holding digital assets across four exchanges and two hot wallets. Ninety days later, we had MPC custody, a whitelisted treasury policy, weekly NAV to our auditor, and our first tokenized T-Bill allocation live — governed by our office, not a vendor\u2019s roadmap.",
    attribution: "Principal · MENA family office · engagement anonymised",
    outcomes: [
      { k: "$180M", l: "migrated to institutional MPC custody" },
      { k: "90 days", l: "from mandate to first on-chain transaction" },
      { k: "3", l: "chains unified under one reporting layer" },
      { k: "0", l: "audit exceptions in first quarterly review" },
    ],
    arc: [
      { w: "D 0—14", t: "Mandate & policy design", d: "Custody model, threshold policy, jurisdiction mapping." },
      { w: "D 14—45", t: "Vault stand-up", d: "MPC signers, whitelist, cold/warm architecture, insurance." },
      { w: "D 45—75", t: "Migration", d: "Exchange & hot-wallet consolidation into institutional custody." },
      { w: "D 75—90", t: "First on-chain treasury", d: "Tokenized T-Bill allocation live · weekly NAV reporting." },
    ],
    frameworks: ["ADGM", "VARA"],
  },
  {
    id: "case-02",
    file: "Advik-Case-02-Singapore-Multi-Family-Office.pdf",
    label: "Case 02",
    tab: "APAC · Singapore",
    title: "A Singapore multi-family office, with a working impact desk in sixty days.",
    snapshot: [
      ["Entity", "Multi-family office · pooled principal capital"],
      ["Region", "Singapore (MAS · VCC)"],
      ["Mandate", "Climate & impact allocation + technology diligence for LP tickets"],
      ["Modules", "M02 Innovation Hub · M03 Sustainability & Impact Desk"],
    ],
    quote:
      "We had conviction on the energy transition but no operational muscle to underwrite it. Sixty days later we had a working impact desk our LPs and auditor could both defend — SFDR-aligned, MAS-comfortable, and on our reporting cadence.",
    attribution: "CIO · Singapore multi-family office · engagement anonymised",
    outcomes: [
      { k: "$220M", l: "impact-mandated AUM classified & reported" },
      { k: "60 days", l: "from mandate to first IC-ready memo" },
      { k: "18", l: "LP allocations underwritten in six months" },
      { k: "100%", l: "SFDR / IFC alignment on new tickets" },
    ],
    arc: [
      { w: "D 0—14", t: "Mandate & thesis", d: "Climate thesis, SFDR classification, KPI selection." },
      { w: "D 14—40", t: "Diligence framework", d: "Chemistry, supply-chain, and tech-readiness scorecards." },
      { w: "D 40—60", t: "First IC memo", d: "Renewables & mobility allocation reviewed, IC-approved." },
      { w: "D 60—90", t: "Reporting layer", d: "Quarterly impact + IRR reporting to LPs and auditor." },
    ],
    frameworks: ["MAS", "MiCA"],
  },
];

const FILTERS = [
  { key: "ALL", label: "All" },
  { key: "ADGM", label: "ADGM · FSRA" },
  { key: "VARA", label: "Dubai · VARA" },
  { key: "MAS", label: "MAS · PSA" },
  { key: "MiCA", label: "MiCA · CASP" },
];

function generatePdf(c) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;

  doc.setFillColor(244, 239, 230);
  doc.rect(0, 0, pageW, pageH, "F");
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(2);
  doc.line(margin, margin, margin + 36, margin);

  doc.setFontSize(8);
  doc.setTextColor(120, 110, 90);
  doc.setFont("helvetica", "normal");
  doc.text(
    `ADVIK VENTURES · ${c.label.toUpperCase()} · ANONYMISED · REDACTED`,
    margin,
    margin + 22
  );
  doc.text("CONFIDENTIAL", pageW - margin, margin + 22, { align: "right" });

  // Title
  doc.setFont("times", "italic");
  doc.setFontSize(24);
  doc.setTextColor(10, 15, 28);
  const titleLines = doc.splitTextToSize(c.title, pageW - margin * 2);
  doc.text(titleLines, margin, margin + 80);
  let y = margin + 80 + titleLines.length * 28 + 30;

  // Snapshot
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(150, 120, 40);
  doc.text("SNAPSHOT", margin, y);
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  c.snapshot.forEach(([k, v]) => {
    doc.setTextColor(140, 130, 110);
    doc.text(k, margin, y);
    doc.setTextColor(10, 15, 28);
    doc.text(v, margin + 80, y);
    y += 16;
  });

  // Quote
  y += 14;
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(1);
  doc.line(margin, y, margin + 24, y);
  y += 16;
  doc.setFont("times", "italic");
  doc.setFontSize(13);
  doc.setTextColor(10, 15, 28);
  const quote = doc.splitTextToSize(`\u201C${c.quote}\u201D`, pageW - margin * 2);
  doc.text(quote, margin, y);
  y += quote.length * 17 + 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120, 110, 90);
  doc.text(c.attribution.toUpperCase(), margin, y);
  y += 22;

  // Outcomes
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(150, 120, 40);
  doc.text("OUTCOMES", margin, y);
  y += 14;
  const boxW = (pageW - margin * 2 - 12 * 3) / 4;
  const boxH = 68;
  c.outcomes.forEach((o, i) => {
    const x = margin + i * (boxW + 12);
    doc.setDrawColor(210, 200, 180);
    doc.setLineWidth(0.5);
    doc.rect(x, y, boxW, boxH);
    doc.setFont("times", "normal");
    doc.setFontSize(18);
    doc.setTextColor(180, 140, 40);
    doc.text(o.k, x + 10, y + 26);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(90, 80, 60);
    const label = doc.splitTextToSize(o.l.toUpperCase(), boxW - 20);
    doc.text(label, x + 10, y + 42);
  });
  y += boxH + 26;

  // Arc
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(150, 120, 40);
  doc.text("90-DAY ARC", margin, y);
  y += 14;
  c.arc.forEach((s) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(4, 120, 87);
    doc.text(s.w, margin, y);
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    doc.setTextColor(10, 15, 28);
    doc.text(s.t, margin + 70, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90, 80, 60);
    const desc = doc.splitTextToSize(s.d, pageW - margin * 2 - 70);
    doc.text(desc, margin + 70, y + 13);
    y += 13 + desc.length * 11 + 6;
  });

  // Footer
  const footY = pageH - 40;
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(0.5);
  doc.line(margin, footY - 14, pageW - margin, footY - 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(120, 110, 90);
  doc.text(
    "ADVIK VENTURES · avadh@advikvc.com · CONFIDENTIAL · FOR INTERNAL CIRCULATION",
    margin,
    footY
  );
  doc.text(
    new Date().getFullYear() + " · " + new Date().toLocaleDateString(),
    pageW - margin,
    footY,
    { align: "right" }
  );

  doc.save(c.file);
}

export default function CaseVignette() {
  const [filter, setFilter] = useState("ALL");
  const filtered =
    filter === "ALL" ? cases : cases.filter((cs) => cs.frameworks.includes(filter));
  const [idx, setIdx] = useState(0);
  const [ndaOpen, setNdaOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const safeIdx = Math.min(idx, Math.max(0, filtered.length - 1));
  const c = filtered[safeIdx];

  const requestDownload = () => {
    if (!c) return;
    setAgreed(false);
    setNdaOpen(true);
  };
  const confirmDownload = () => {
    if (!c) return;
    if (!agreed) {
      toast.error("Please acknowledge the confidentiality note to proceed.");
      return;
    }
    try {
      generatePdf(c);
      setNdaOpen(false);
      toast.success(`${c.label} one-pager downloaded. Redacted for internal circulation.`);
    } catch (e) {
      console.error(e);
      toast.error("Could not generate PDF. Please try again.");
    }
  };

  return (
    <section
      id="case"
      className="relative py-24 md:py-32 border-t border-[var(--line)] bg-ink-2 overflow-hidden"
      data-testid="case-vignette-section"
    >
      <div className="grain absolute inset-0" />
      <div className="pointer-events-none absolute -right-10 top-1/2 -translate-y-1/2 font-display text-[22vw] md:text-[16vw] leading-none text-ivory/[0.04] select-none">
        {c ? c.label : "Cases"}
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Framework filter */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim mr-2">
            Filter · framework
          </span>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => {
                setFilter(f.key);
                setIdx(0);
              }}
              data-testid={`case-filter-${f.key.toLowerCase()}`}
              className={`text-[10px] tracking-eyebrow uppercase px-2.5 py-1.5 border transition-colors ${
                filter === f.key
                  ? "border-emerald text-emerald bg-emerald/10"
                  : "border-[var(--line-strong)] text-ivory/60 hover:text-ivory hover:border-ivory/40"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Case tab selector */}
        <div className="flex items-center gap-2 mb-10 md:mb-12 flex-wrap">
          <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim mr-2">
            Cases
          </span>
          {filtered.length === 0 && (
            <span className="text-[11px] tracking-eyebrow uppercase text-ivory-dim italic">
              No published case for this framework yet — request a private briefing.
            </span>
          )}
          {filtered.map((cs, i) => (
            <button
              key={cs.id}
              onClick={() => setIdx(i)}
              data-testid={`case-tab-${i}`}
              className={`text-[11px] tracking-eyebrow uppercase px-3 py-2 border transition-colors ${
                i === safeIdx
                  ? "border-gold text-gold bg-gold/10"
                  : "border-[var(--line-strong)] text-ivory/70 hover:text-ivory hover:border-ivory/40"
              }`}
            >
              {cs.label} · {cs.tab}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {c && (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
              <div className="md:col-span-6">
                <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-4">
                  {c.label} · Anonymised
                </div>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                  {c.title.split(" ").slice(0, -3).join(" ")}{" "}
                  <em className="text-gold not-italic">
                    {c.title.split(" ").slice(-3, -1).join(" ")}
                  </em>{" "}
                  {c.title.split(" ").slice(-1)}
                </h2>
              </div>
              <div className="md:col-span-5 md:col-start-8 md:pt-6 text-ivory/70 leading-relaxed">
                <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim mb-3">
                  Snapshot
                </div>
                <ul className="space-y-1 text-sm">
                  {c.snapshot.map(([k, v]) => (
                    <li key={k}>
                      <span className="text-ivory-dim">{k}</span> · {v}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-7">
                <blockquote className="font-display text-2xl md:text-3xl lg:text-[38px] leading-[1.2] tracking-tight text-ivory">
                  <span className="text-gold mr-1">“</span>
                  {c.quote}
                  <span className="text-gold ml-1">”</span>
                </blockquote>
                <div className="hairline my-6 max-w-md" />
                <div className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                  {c.attribution}
                </div>

                <div className="mt-10 md:mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  {c.outcomes.map((o, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 0.6, delay: i * 0.08 }}
                      className="border border-[var(--line)] p-4 md:p-5 hover:border-gold/50 transition-colors"
                      data-testid={`case-outcome-${i}`}
                    >
                      <div className="font-display text-3xl md:text-4xl text-gold leading-none">
                        {o.k}
                      </div>
                      <div className="mt-2 text-[10px] tracking-eyebrow uppercase text-ivory-dim leading-tight">
                        {o.l}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="border border-[var(--line)] bg-ink/60 p-6 md:p-8">
                  <div className="text-[10px] tracking-eyebrow uppercase text-gold mb-4">
                    90-day arc
                  </div>
                  <ol className="space-y-5">
                    {c.arc.map((s, i) => (
                      <li key={i} className="grid grid-cols-12 gap-3 items-baseline">
                        <span className="col-span-3 text-[10px] tracking-eyebrow uppercase text-emerald">
                          {s.w}
                        </span>
                        <div className="col-span-9">
                          <div className="font-display text-lg text-ivory leading-tight">
                            {s.t}
                          </div>
                          <div className="text-xs text-ivory/60 mt-1 leading-relaxed">
                            {s.d}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-6 pt-4 border-t border-[var(--line)] flex items-center justify-between gap-3">
                    <span className="text-[10px] tracking-eyebrow uppercase text-ivory-dim">
                      Details available under NDA
                    </span>
                    <a
                      href="#workshop"
                      data-testid={`case-cta-${idx}`}
                      className="text-[10px] tracking-eyebrow uppercase text-gold gold-underline"
                    >
                      Discuss your mandate →
                    </a>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={requestDownload}
                  data-testid="case-download-pdf"
                  className="group mt-4 w-full border border-gold/60 bg-transparent hover:bg-gold hover:text-ink text-gold px-5 py-4 flex items-center justify-between gap-3 transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <Download size={16} strokeWidth={1.5} />
                    <span className="flex flex-col items-start leading-tight">
                      <span className="text-[10px] tracking-eyebrow uppercase opacity-80">
                        {c.label} · Redacted one-pager
                      </span>
                      <span className="font-display text-lg">
                        Download for internal circulation
                      </span>
                    </span>
                  </span>
                  <span className="text-lg transition-transform group-hover:translate-x-1">→</span>
                </button>
              </div>
            </div>
          </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* NDA gate */}
      <Dialog open={ndaOpen} onOpenChange={setNdaOpen}>
        <DialogContent
          className="!bg-ink !text-ivory !border-[var(--line-strong)] max-w-lg p-0 overflow-hidden"
          data-testid="nda-dialog"
        >
          <div className="grain absolute inset-0 pointer-events-none" />
          <div className="relative p-8 md:p-10">
            <div className="flex items-center gap-2 text-[10px] tracking-eyebrow uppercase text-gold">
              <ShieldCheck size={12} strokeWidth={1.5} />
              Confidentiality note
            </div>
            <DialogTitle asChild>
              <h3 className="mt-4 font-display text-2xl md:text-3xl leading-tight text-ivory tracking-tight">
                Before you download.
              </h3>
            </DialogTitle>
            <DialogDescription asChild>
              <p className="mt-3 text-ivory/70 text-sm leading-relaxed">
                This one-pager is anonymised and redacted, but describes a live
                engagement. It is provided for your internal circulation only.
              </p>
            </DialogDescription>

            <label className="mt-6 flex items-start gap-3 cursor-pointer group">
              <span
                className={`mt-0.5 h-5 w-5 border shrink-0 grid place-items-center transition-colors ${
                  agreed
                    ? "bg-gold border-gold"
                    : "border-[var(--line-strong)] group-hover:border-ivory/50"
                }`}
              >
                {agreed && <Check size={12} strokeWidth={2.5} className="text-ink" />}
              </span>
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="sr-only"
                data-testid="nda-checkbox"
              />
              <span className="text-sm text-ivory/85 leading-snug">
                I confirm this document is for internal circulation only and will
                not be shared externally without Advik Ventures&rsquo; written consent.
              </span>
            </label>

            <div className="mt-8 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setNdaOpen(false)}
                data-testid="nda-cancel"
                className="text-[10px] tracking-eyebrow uppercase text-ivory-dim hover:text-ivory flex items-center gap-1"
              >
                <X size={12} />
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDownload}
                disabled={!agreed}
                data-testid="nda-confirm"
                className="group inline-flex items-center gap-2 px-5 py-3 bg-gold text-ink font-medium tracking-wide hover:bg-gold-soft transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Agree & download
                <Download size={14} strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
